import { Modal, useModals } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import { ExternalIntegrationsClient, GoogleAdsIntegration } from 'api/clients/ExternalIntegrationsClient'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useMutation } from 'react-query'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import Styles from './GoogleAdsExistingCampaignsModal.module.scss'

interface Props {
  integration: GoogleAdsIntegration
  onClose: () => void
}

export const GoogleAdsExistingCampaignsModal = ({ integration, onClose }: Props) => {
  const { closeModal } = useModals()
  const workspaceId = useCurrentWorkspaceId()
  const campaigns = integration.additional_data?.existing_campaigns_summary ?? []

  const { mutateAsync: importCampaigns, isLoading } = useMutation(() =>
    ExternalIntegrationsClient.importExistingCampaigns({ workspaceId, id: integration.id }),
  )

  const handleManageExisting = async () => {
    try {
      await importCampaigns()
      closeModal()
      onClose()
    } catch {
      showToastMessage('Failed to import campaigns. Please try again.', 'error')
    }
  }

  const handleStartFresh = () => {
    closeModal()
    onClose()
  }

  return (
    <Modal.Root
      size="sm"
      onPressOutside={() => {}}
      onClose={() => {}}
      aria-label="Existing Google Ads Campaigns Detected"
    >
      <Modal.Header title="Existing Google Ads Campaigns Detected" />
      <Modal.Content>
        <div className={Styles.content}>
          <Text>
            We found {campaigns.length} active campaign{campaigns.length !== 1 ? 's' : ''} in your Google Ads account.
            Would you like Blaze to manage them, or start fresh with new campaigns?
          </Text>
          <div className={Styles.campaignList}>
            {campaigns.map((campaign) => (
              <div key={campaign.id} className={Styles.campaignItem}>
                <span className={Styles.campaignName}>{campaign.name}</span>
              </div>
            ))}
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="tertiary" onPress={handleStartFresh} isDisabled={isLoading}>
            Start Fresh
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton variant="primary" onPress={handleManageExisting} isDisabled={isLoading}>
            {isLoading ? 'Importing...' : 'Manage Existing'}
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
